/**
 * Shared helpers for walking `ControlConfiguration.elementPaths` into a
 * composition placement's `elements` tree.
 *
 * Three call sites read this:
 *   - `extractTranslatableElements` (bulk translation target collection)
 *   - `extractCompositionQuickEditFields` (popover row collection, PLAT-9968)
 *   - `setCompositionFieldValue` (popover save, PLAT-9968)
 *
 * Each path is a sequence of array indices into a node's `elements` array.
 * `[0, 2]` means `root.elements[0].elements[2]`.
 */

/**
 * Walk `path` into `root`'s nested `elements` tree and return the leaf
 * node, or `null` if any segment is out of range / missing.
 */
export function traverseCompositionElementPath(
  root: Record<string, unknown>,
  path: ReadonlyArray<number>,
): Record<string, unknown> | null {
  let node: unknown = root;
  for (const segment of path) {
    if (!node || typeof node !== 'object') return null;
    const els = (node as { elements?: unknown }).elements;
    if (!Array.isArray(els)) return null;
    const idx = Number(segment);
    if (!Number.isInteger(idx) || idx < 0 || idx >= els.length) return null;
    node = els[idx];
  }
  if (!node || typeof node !== 'object') return null;
  return node as Record<string, unknown>;
}

/**
 * Resolve an active-element id (as fired by `Renderer.onActiveElementsChange`)
 * to the **containing composition placement**.
 *
 * When the user clicks a leaf text node inside a composition (e.g. the
 * "OPERATING CAPACITY" text inside a stat-callout composition), the
 * Creatomate iframe fires the leaf's id, not the wrapping composition's.
 * Quick-edit lookups are keyed by composition placement id (the
 * `extraElementData` bucket lives there), so the canvas action layer
 * needs to walk up.
 *
 * Returns the composition placement id when found, the active id itself
 * when it already IS a top-level composition, or null when no enclosing
 * composition exists (e.g. the user clicked a standalone text overlay).
 */
export function findContainingCompositionId(
  videoSourceElements: ReadonlyArray<Record<string, unknown>> | null | undefined,
  activeId: string,
): string | null {
  if (!Array.isArray(videoSourceElements) || videoSourceElements.length === 0) {
    return null;
  }
  for (const el of videoSourceElements) {
    if (!el || typeof el !== 'object') continue;
    const elType = (el as { type?: unknown }).type;
    if (elType !== 'composition') continue;
    const elId = (el as { id?: unknown }).id;
    if (typeof elId !== 'string') continue;
    if (elId === activeId) return elId;
    if (subtreeContainsId(el, activeId)) return elId;
  }
  return null;
}

function subtreeContainsId(
  node: Record<string, unknown>,
  targetId: string,
): boolean {
  return !!findNodeInTree(
    ((node as { elements?: unknown }).elements as
      | ReadonlyArray<Record<string, unknown>>
      | undefined) ?? [],
    targetId,
  );
}

/**
 * Recursive id lookup over a videoSource `elements` tree (or any subset).
 *
 * Returns the first node with `id === targetId`. When `predicate` is
 * supplied, additionally requires the node to satisfy it — used by
 * callers that want "first node with this id AND a non-empty `text`
 * property" without walking the tree twice. Walks depth-first; returns
 * null when no node matches.
 *
 * Shared by `findContainingCompositionId` (above) and by the canvas
 * action layer's geometry + text-overlay resolution (PLAT-9968) so we
 * don't drift three recursive-walk implementations.
 */
export function findNodeInTree(
  elements: ReadonlyArray<Record<string, unknown>>,
  targetId: string,
  predicate?: (node: Record<string, unknown>) => boolean,
): Record<string, unknown> | null {
  for (const el of elements) {
    if (!el || typeof el !== 'object') continue;
    if ((el as { id?: unknown }).id === targetId) {
      if (!predicate || predicate(el)) return el;
    }
    const children = (el as { elements?: unknown }).elements;
    if (Array.isArray(children)) {
      const found = findNodeInTree(
        children as ReadonlyArray<Record<string, unknown>>,
        targetId,
        predicate,
      );
      if (found) return found;
    }
  }
  return null;
}

/**
 * Depth-first search `root`'s nested `elements` tree for the first node with a
 * non-empty string `text` property, returning both the node and the index
 * `path` from `root` to it (the same `[i, j, …]` shape
 * `traverseCompositionElementPath` consumes).
 *
 * Used by the quick-edit fallback to synthesize an editable field for a
 * composition that has NO `allowQuickEdit` controls authored: the editable
 * text lives on a nested leaf, and the save path
 * (`modifyCompositionProperty` → `editor.timeline.getElement`) can only look
 * up TOP-LEVEL element ids. So the synthesized field must target the
 * top-level placement and carry the path down to the leaf — exactly like an
 * `allowQuickEdit` control's `elementPaths[0]` — rather than the leaf id with
 * an empty path (which can't be resolved and silently fails to save).
 *
 * Returns `null` when no text-bearing descendant exists. `root` itself is
 * NOT considered — callers handle the "the element itself carries text"
 * (top-level overlay) case separately, where the path is empty.
 */
export function findFirstTextLeafPath(
  root: Record<string, unknown>,
): { node: Record<string, unknown>; path: number[] } | null {
  const walk = (
    node: Record<string, unknown>,
    path: number[],
  ): { node: Record<string, unknown>; path: number[] } | null => {
    const children = (node as { elements?: unknown }).elements;
    if (!Array.isArray(children)) return null;
    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      if (!child || typeof child !== 'object') continue;
      const childRecord = child as Record<string, unknown>;
      const childPath = [...path, i];
      if (hasNonEmptyTextProperty(childRecord)) {
        return { node: childRecord, path: childPath };
      }
      const nested = walk(childRecord, childPath);
      if (nested) return nested;
    }
    return null;
  };
  return walk(root, []);
}

/**
 * Predicate for `findNodeInTree` selecting nodes with a non-empty string
 * `text` property. Shared by overlay-resolution and field-extraction
 * paths that need "any text-bearing node with this id".
 */
export function hasNonEmptyTextProperty(
  node: Record<string, unknown>,
): boolean {
  const text = (node as { text?: unknown }).text;
  return typeof text === 'string' && text.trim() !== '';
}

/**
 * Walk the source-elements tree and collect every node whose
 * `[time, time + duration]` overlaps `[rangeStart, rangeEnd]` AND carries
 * a non-empty string `text` property.
 *
 * A transcript-underlined time range can host multiple top-level
 * elements (an arrow PNG + a text overlay + an image), and the
 * `useTimelineMarkers` marker id points to only ONE of them — often not
 * the editable one. Callers use this to find text-bearing siblings when
 * a direct id lookup misses.
 *
 * Time fields on Creatomate source elements: `time` (seconds, start)
 * and `duration` (seconds). Elements missing `time` are skipped — we
 * never anchor undated elements to `t=0`, which would falsely match any
 * marker beginning at the clip start.
 */
export function collectTextElementsInTimeRange(
  elements: ReadonlyArray<Record<string, unknown>>,
  rangeStart: number,
  rangeEnd: number,
): Record<string, unknown>[] {
  const matches: Record<string, unknown>[] = [];
  const walk = (nodes: ReadonlyArray<Record<string, unknown>>) => {
    for (const el of nodes) {
      if (!el || typeof el !== 'object') continue;
      if (hasNonEmptyTextProperty(el)) {
        const start = (el as { time?: unknown }).time;
        if (typeof start === 'number') {
          const dur = (el as { duration?: unknown }).duration;
          const elEnd =
            typeof dur === 'number' && Number.isFinite(dur)
              ? start + dur
              : start;
          // Overlap test: ranges [start, elEnd] and [rangeStart, rangeEnd]
          // intersect when start <= rangeEnd && elEnd >= rangeStart.
          if (start <= rangeEnd && elEnd >= rangeStart) {
            matches.push(el);
          }
        }
      }
      const children = (el as { elements?: unknown }).elements;
      if (Array.isArray(children)) {
        walk(children as ReadonlyArray<Record<string, unknown>>);
      }
    }
  };
  walk(elements);
  return matches;
}
